import moment from "moment";
import defaultGoals from "./data/goals";
import { randomImageId } from "./components/goal-card-views/GoalIcon";
import AuthService from "./services/AuthService";
import { logEvent, setCurrentScreen, setUserId, setUserProperties } from "./services/analytics";
import { setConsultCallback } from "./lib/consultCallback";

import { v4 as uuid } from "uuid";
import React from "react";

export { logEvent, setCurrentScreen, setUserId };

const navigateRef = React.createRef();

export function setNavigateRef(navigate) {
  navigateRef.current = navigate;
}

export default () => {
  return {
    initialize({ set }) {
      set({
        goals: defaultGoals,
        filterMonth: moment().format("MMMM"),
        filterYear: moment().format("YYYY"),
        filterDay: "14",
        isErrorModalVisible: false,
        children: []
      });
    },

    logout({ set, dispatch }) {
      dispatch("navigate", { pathname: "/login" });
      AuthService.logout();
      set({ loggedIn: false });
      set({ user: { loggedOut: true } });
      AuthService.removeAuthToken();
      logEvent("logOutSuccess");
      sessionStorage.removeItem("familyId"); // perform after last analytics call
    },

    navigate2FA({ set, dispatch }, { pathname, params }) {
      set({ email: params.email, password: params.password });
      dispatch("navigate", { pathname, options: { params } });
    },

    setUser({ set }, user) {
      set({ user });
    },

    setConsultView({ set }, consultView) {
      set({ consultView });
    },

    setCareScreenerSubmissionId({ set }, careScreenerSubmissionId) {
      set({ careScreenerSubmissionId });
    },

    setTypeformResponseId({ set }, typeformResponseId) {
      set({ typeformResponseId });
    },

    setUserId({ set }, userId) {
      set({ userId });
      setUserId(userId);
    },

    setPatient({ set, dispatch }, { pathname, params }) {
      set({ patient: params.patient });
      if (pathname) {
        dispatch("navigate", { pathname, options: { params } });
      }
    },

    setPatientData({ set }, { data }) {
      set({ patientData: data });
    },

    setChildren({ set }, { children }) {
      set({ children });
    },

    setGoal({ set }, { params }) {
      set({ goal: params.goal });
    },

    setReward({ set }, { params }) {
      set({ reward: params.reward });
    },

    setOnCancel({ set }, { params }) {
      set({ onCancel: params.onCancel });
    },

    setFilter({ set }, { filter }) {
      set({ filter });
    },

    setFilterMonth({ set }, { filterMonth }) {
      set({ filterMonth });
    },

    setFilterYear({ set }, { filterYear }) {
      set({ filterYear });
    },

    setFilterDay({ set }, { filterDay }) {
      set({ filterDay });
    },

    setAssessments({ set }, { assessments }) {
      set({ assessments });
    },

    setMessage({ set }, { message }) {
      set({ message });
    },

    setOpenDefaultRewardEditScreen({ set }, { params }) {
      set({ openDefaultRewardEditScreen: params.openDefaultRewardEditScreen });
    },

    setOpenCreateGoalScreen({ set }, { params }) {
      set({ openCreateGoalScreen: params.openCreateGoalScreen });
    },

    setOpenAssignedGoalScreen({ set }, { params }) {
      set({ openAssignedGoalScreen: params.openAssignedGoalScreen });
    },

    setOpenCustomGoalScreen({ set }, { params }) {
      set({ openCustomGoalScreen: params.openCustomGoalScreen });
    },

    setOpenStandardGoalScreen({ set }, { params }) {
      set({ openStandardGoalScreen: params.openStandardGoalScreen });
    },

    setPatientRefetch({ set }, { params }) {
      set({ patientRefetch: params.patientRefetch });
    },

    continueInvite({ set, dispatch }, { pathname, params }) {
      set({ inviteAccountType: params.inviteAccountType });
      dispatch("navigate", { pathname });
    },

    setConsultCallback({ dispatch }, { params }) {
      setConsultCallback(params.consultCallback);
      dispatch("navigate", { pathname: "/forgotPassword", options: { params } });
    },

    // eslint-disable-next-line no-empty-pattern
    navigate({}, { pathname }) {
      if (navigateRef.current) {
        navigateRef.current(pathname);
      } else {
        console.log(`unable to navigate to ${pathname} due to undefined navigateRef`);
      }
    },

    async createGoal({ get, set }, goal) {
      const { goals } = get();
      const maxGoalId = goals && goals.length ? Math.max(...goals.map(g => g.id || 0)) : 0;
      const newGoal = {
        ...goal,
        id: maxGoalId + 1,
        custom: true,
        theme: "nightSky",
        animal: randomImageId()
      };
      const nextGoals = goals.concat([newGoal]);
      set({ goals: nextGoals });
    },

    async createReward({ get, set }, reward) {
      const { rewards } = get();
      const maxRewardId = rewards && rewards.length ? Math.max(...rewards.map(r => r.id || 0)) : 0;
      const newReward = {
        ...reward,
        id: maxRewardId + 1,
        custom: true
      };
      const nextRewards = rewards.concat([newReward]);
      set({ rewards: nextRewards });
    },

    /* eslint-disable no-empty-pattern */
    setProvider({}, providerName) {
      setUserProperties({ healthcare_provider: providerName });
    },

    setUserRole({}, role) {
      setUserProperties({ user_role: role });
    },

    setCurrentScreen({}, screen) {
      setCurrentScreen(screen);
    },

    logEvent({}, event, obj) {
      logEvent(event, obj);
    },
    /* eslint-enable no-empty-pattern */

    showModal({ set }, message) {
      set({ error: { message } });
      set({ isErrorModalVisible: true });
    },

    hideModal({ set }) {
      set({ isErrorModalVisible: false });
    },

    displayToast({ get, set }, toast) {
      let { activeToasts } = get();
      activeToasts = activeToasts ? activeToasts : [];

      if (!toast.id) {
        toast.id = uuid();
      }
      activeToasts.push(toast);

      const newActiveToasts = [...activeToasts];
      set({ activeToasts: newActiveToasts });
      const { toastRender } = get();

      if (toastRender) {
        toastRender(newActiveToasts);
      }
    },
    removeToast({ get, set }, toast) {
      let { activeToasts } = get();
      activeToasts = activeToasts ? activeToasts : [];

      const index = activeToasts.findIndex(t => t.id === toast.id);
      if (index >= 0) {
        activeToasts.splice(index, 1);
      }
      const newActiveToasts = [...activeToasts];
      set({ activeToasts: newActiveToasts });
      const { toastRender } = get();
      if (toastRender) {
        toastRender(newActiveToasts);
      }
    },
    setToastRender({ set }, renderFunction) {
      set({ toastRender: renderFunction });
    }
  };
};
